/**
 * Client networking + clock synchronisation (see §11).
 *
 * The server's clock is authoritative. On connect we run a short ping/echo
 * handshake to estimate our offset from server time, then convert every
 * scheduled timestamp into a local one so all phones fire in sync.
 */

import { TIMING } from '../shared/tuning.js';
import type { ClientMsg, ServerMsg } from '../shared/protocol.js';

type Handler = (msg: ServerMsg) => void;

export class Net {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  /** offset = serverTime - clientTime. serverNow ≈ Date.now() + offset. */
  offset = 0;
  /** Best (lowest) round-trip seen during sync — a rough quality signal. */
  bestRtt = Infinity;
  connected = false;

  private pingId = 0;
  private pending = new Map<number, number>(); // id -> client send time

  constructor(private url: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      ws.onopen = () => {
        this.connected = true;
        resolve();
      };
      ws.onerror = (e) => {
        if (!this.connected) reject(e);
      };
      ws.onclose = () => {
        this.connected = false;
        this.emit({ t: 'error', message: '__disconnected__' });
      };
      ws.onmessage = (ev) => {
        let msg: ServerMsg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        // Intercept pong for clock sync; pass everything else through.
        if (msg.t === 'pong') {
          this.onPong(msg.id, msg.serverTime);
          return;
        }
        this.emit(msg);
      };
    });
  }

  onMessage(h: Handler): void {
    this.handlers.add(h);
  }

  private emit(msg: ServerMsg): void {
    for (const h of this.handlers) h(msg);
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  /** Current best estimate of server time, in ms. */
  serverNow(): number {
    return Date.now() + this.offset;
  }

  /** Convert a server timestamp to a local (Date.now) timestamp. */
  toLocal(serverTime: number): number {
    return serverTime - this.offset;
  }

  /**
   * Run the clock handshake: several spaced pings; keep the offset from the
   * lowest-RTT sample (least jitter). Resolves when done.
   */
  async syncClock(): Promise<void> {
    for (let i = 0; i < TIMING.CLOCK_SAMPLES; i++) {
      const id = ++this.pingId;
      this.pending.set(id, Date.now());
      this.send({ t: 'ping', id, clientTime: Date.now() });
      await sleep(TIMING.CLOCK_SAMPLE_GAP_MS);
    }
    // Give the last pong a moment to land.
    await sleep(TIMING.CLOCK_SAMPLE_GAP_MS);
  }

  private onPong(id: number, serverTime: number): void {
    const t0 = this.pending.get(id);
    if (t0 === undefined) return;
    this.pending.delete(id);
    const t3 = Date.now();
    const rtt = t3 - t0;
    // NTP-style offset: serverTime is both t1 and t2 (server echoes instantly).
    const offset = serverTime - (t0 + t3) / 2;
    if (rtt < this.bestRtt) {
      this.bestRtt = rtt;
      this.offset = offset;
    }
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
